import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fetchAuditedTableData, updateAuditedTableData } from '../../../../store/company/action';
import { getTimeStamp } from '../../../../utils/dateUtils';
import { removeKeyAndConvertIdToNumber } from '../../../../utils/utils';
import { DATA_FETCH_STATUS } from '../../../../store/dashboard/constants';

const AUDITED_POLL_INTERVAL_MS = 5000;

interface UseAuditedStatementDataParams {
    orgId: string;
    /** The backend `misDisplayName` this instance owns (e.g. 'Balance Sheet'). Empty until resolved. */
    metric: string;
    /** The shared Standalone/Consolidated selection's request nature, read at the first fetch. */
    getRequestNature?: () => string | undefined;
}

/**
 * Owns fetch + drill-down + polling + classification-save state for ONE audited-financials
 * statement. Extracted out of AuditedFinancialsVisualisation (RECUR-27010) so the redesigned
 * page can run three independent instances side by side (Balance Sheet / P&L / Cash Flow)
 * while the legacy single-metric report view (AicaPDFReport, driven by `selectedMetric`)
 * keeps using exactly one — same fetch/poll/save behavior either way.
 */
export function useAuditedStatementData({ orgId, metric, getRequestNature }: UseAuditedStatementDataParams) {
    const dispatch = useDispatch();
    const queryParams = useParams();

    const [tableData, setTableData] = useState<any>({});
    const [loading, setLoading] = useState<boolean>(true);
    const [status, setStatus] = useState<string>('');
    const [tableType, setTableType] = useState<string>('');
    const [rawTableData, setRawTableData] = useState<any>({});
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [changedData, setChangedData] = useState<any>([]);

    const pollingIntervalRef = useRef<any>(null);
    const natureRef = useRef<string | undefined>(undefined);
    // The org + metric last fetched, so the first-fetch effect can skip one the nature-change trigger already sent this render.
    const fetchedRequestRef = useRef({ orgId: '', metric: '' });
    // Bumped whenever an in-flight fetchTableData response should no longer be trusted (metric
    // cleared while a FetchAuditedTableData request — takeEvery, not takeLatest — is still out).
    // Same technique as useProvisionalStatementData's requestRef, scaled down since this hook
    // keeps its state as plain fields rather than a single identity-keyed value.
    const requestGenerationRef = useRef(0);

    const fetchTableData = useCallback(
        (nature?: string, isPolling = false) => {
            if (!metric || !orgId) return;
            natureRef.current = nature;
            fetchedRequestRef.current = { orgId, metric };
            if (!isPolling) setLoading(true);
            const requestGeneration = ++requestGenerationRef.current;
            const isStale = () => requestGeneration !== requestGenerationRef.current;
            dispatch(
                fetchAuditedTableData(
                    { type: 'auditedfinancials', mis: metric, orgId, requestId: getTimeStamp(), raw: false, nature },
                    {
                        onError: () => {
                            if (isStale()) return;
                            setLoading(false);
                        },
                        onSuccess: (data: any, statusRes: any) => {
                            if (isStale()) return;
                            setStatus(statusRes?.auditedFinancialsStatus);
                            setLoading(false);
                            setTableData(data);
                        },
                    },
                ),
            );
        },
        [dispatch, metric, orgId],
    );

    const fetchRawData = useCallback(
        (row: { [key: string]: string }, header: { [key: string]: string }) => {
            if (header.name !== 'Classification' || !row?.[header.key] || row.isAggregated) return;
            setLoading(true);
            setTableType(row.classificationName);
            // Shares requestGenerationRef with fetchTableData: a metric reset, or a fresh
            // fetchTableData call, must invalidate an in-flight raw/drill-down request too —
            // both write into state that belongs to the same statement instance.
            const requestGeneration = ++requestGenerationRef.current;
            const isStale = () => requestGeneration !== requestGenerationRef.current;
            dispatch(
                fetchAuditedTableData(
                    {
                        type: 'auditedfinancials',
                        mis: metric,
                        orgId,
                        requestId: getTimeStamp(),
                        classificationId: row.classificationId,
                        raw: true,
                        nature: natureRef.current,
                    },
                    {
                        onError: () => {
                            if (isStale()) return;
                            setLoading(false);
                        },
                        onSuccess: (data: any) => {
                            if (isStale()) return;
                            setLoading(false);
                            setRawTableData(data);
                        },
                    },
                ),
            );
        },
        [dispatch, metric, orgId],
    );

    const resetDrillDown = useCallback(() => {
        setTableType('');
        setIsEditing(false);
        setChangedData([]);
    }, []);

    const handleSave = useCallback(() => {
        dispatch(
            updateAuditedTableData({
                orgId: queryParams.id ?? '',
                metrics: metric,
                changedData: removeKeyAndConvertIdToNumber(changedData, 'name'),
                source: 'AicaAuditedFinancials',
                onSuccess: () => {
                    fetchTableData(natureRef.current);
                    resetDrillDown();
                    toast.success('Data saved successfully');
                },
                onError: () => {
                    toast.error('Failed to save data');
                },
            }),
        );
    }, [dispatch, queryParams.id, metric, changedData, fetchTableData, resetDrillDown]);

    // Initial fetch — fires once the (possibly async-resolved) metric name is known, and
    // again if the metric (the legacy report-mode dropdown) or the org changes.
    useEffect(() => {
        // RECUR-27640 — an org whose misOptions lack this statement leaves `metric` empty forever.
        // Without settling, `loading` stays true (it starts true and is only cleared inside a fetch
        // callback) and the page spins indefinitely. Settle so the empty state renders instead.
        if (!metric) {
            // Reset, don't just settle: if this hook HAD loaded a statement and the metric later
            // goes away (org switch, options refetch), clearing only `loading` leaves the previous
            // org's rows rendering under the fallback title, and a PROCESSING status keeps the poll
            // interval alive against a fetch that no longer runs.
            // Invalidate the in-flight generation too — otherwise a still-pending fetchTableData
            // response can land after this reset and repaint the stale rows/status we're clearing.
            requestGenerationRef.current += 1;
            fetchedRequestRef.current = { orgId: '', metric: '' };
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
            setLoading(false);
            setStatus('');
            setTableData({});
            setRawTableData({});
            setTableType('');
            setIsEditing(false);
            setChangedData([]);
            return;
        }
        if (fetchedRequestRef.current.orgId === orgId && fetchedRequestRef.current.metric === metric) return;
        fetchTableData(getRequestNature ? getRequestNature() : natureRef.current);
        // fetchTableData is a stable useCallback memoised on [dispatch, metric, orgId] — both are
        // already deps here, so its identity is covered; omitting it avoids a redundant re-run.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [metric, orgId]);

    // Poll while the backend is still generating this statement.
    useEffect(() => {
        if (status === DATA_FETCH_STATUS.PROCESSING) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = setInterval(() => {
                fetchTableData(natureRef.current, true);
            }, AUDITED_POLL_INTERVAL_MS);
        } else {
            clearInterval(pollingIntervalRef.current);
        }
        return () => clearInterval(pollingIntervalRef.current);
    }, [status, fetchTableData]);

    return {
        tableData,
        setTableData,
        loading,
        status,
        tableType,
        setTableType,
        rawTableData,
        isEditing,
        setIsEditing,
        changedData,
        setChangedData,
        fetchTableData,
        fetchRawData,
        resetDrillDown,
        handleSave,
    };
}
